import { createRoute, Outlet } from "@tanstack/react-router";
import { Home } from "./Home";
import { Accounts } from "./Accounts";
import { Users } from "./Users";
import { memo } from "react";
import { useIntl } from "react-intl";
import { memoize } from "es-toolkit";
import { MenuType } from "../../types/menu";
import { IconDashboard, IconHome, IconMap, IconMessage } from "@tabler/icons-react";
import { adminRoutes } from "../../App";
import { Chats } from "./Chats";
import { MapInspectDashboardLayout } from "../../layouts/MapInspectDashboardLayout";
import { MapSettings } from "../app/map/Settings";
import { MapLayers } from "../app/map/Layers";
import { MapSources } from "../app/map/Sources";
import { MapVersions } from "../app/map/Versions";
import { AIMap } from "../app/map/AIMap";
import { Maps } from "./Maps";
import { Usage } from "./Usage";
import { DashboardLayout } from "../../layouts/DashboardLayout";
import type { IntlShape } from "react-intl";
import type { NavMenuOptions } from "../../types/menu";

export const adminInspectMapChatLayoutRoute = createRoute({
  getParentRoute: () => adminRoutes,
  path: "/inspect/$accountName/chats/$chatId",
  component: memo(() => {
    return (
      <MapInspectDashboardLayout>
        <Outlet />
      </MapInspectDashboardLayout>
    );
  }),
});

export const adminInspectMapLayoutRoute = createRoute({
  getParentRoute: () => adminRoutes,
  path: "/inspect/$accountName/maps/$map",
  component: memo(() => {
    return (
      <MapInspectDashboardLayout>
        <Outlet />
      </MapInspectDashboardLayout>
    );
  }),
});

const rootNav = memoize((intl: IntlShape): NavMenuOptions => ({
  items: [
    {
      title: intl.formatMessage({
        defaultMessage: "Home",
        id: "navigation.home",
      }),
      type: MenuType.Single,
      Icon: IconHome,
      to: "/admin",
    },
    {
      title: intl.formatMessage({
        defaultMessage: "Accounts",
        id: "navigation.accounts",
      }),
      type: MenuType.Single,
      Icon: IconHome,
      to: "/admin/accounts",
    },
    {
      title: intl.formatMessage({
        defaultMessage: "Users",
        id: "navigation.users",
      }),
      type: MenuType.Single,
      Icon: IconDashboard,
      to: "/admin/users",
    },
    {
      title: intl.formatMessage({
        defaultMessage: "Maps",
        id: "navigation.maps",
      }),
      type: MenuType.Single,
      Icon: IconMap,
      to: "/admin/maps",
    },
    {
      title: intl.formatMessage({
        defaultMessage: "Chats",
        id: "navigation.chats",
      }),
      type: MenuType.Single,
      Icon: IconMessage,
      to: "/admin/chats",
    },
    {
      title: intl.formatMessage({
        defaultMessage: "Usage",
        id: "navigation.usage",
      }),
      type: MenuType.Single,
      Icon: IconMessage,
      to: "/admin/usage",
    },
  ],
}), { getCacheKey: (intl: IntlShape) => intl.locale });

const adminDashboardLayoutRoute = createRoute({
  getParentRoute: () => adminRoutes,
  id: "dashboard",
  component: memo(() => {
    const intl = useIntl();
    return (
      <DashboardLayout menu={rootNav(intl)}>
        <Outlet />
      </DashboardLayout>
    );
  }),
});
const adminIndexRoute = createRoute({
  getParentRoute: () => adminDashboardLayoutRoute,
  path: "/",
  component: Home,
});
const adminAccountsRoute = createRoute({
  getParentRoute: () => adminDashboardLayoutRoute,
  path: "/accounts",
  component: Accounts,
});
const adminUsersRoute = createRoute({
  getParentRoute: () => adminDashboardLayoutRoute,
  path: "/users",
  component: Users,
});

const adminChatsRoute = createRoute({
  getParentRoute: () => adminDashboardLayoutRoute,
  path: "/chats",
  component: Chats,
});
const adminMapsRoute = createRoute({
  getParentRoute: () => adminDashboardLayoutRoute,
  path: "/maps",
  component: Maps,
});
const adminUsageRoute = createRoute({
  getParentRoute: () => adminDashboardLayoutRoute,
  path: "/usage",
  component: Usage,
});

const adminMapInspectSettingsRoute = createRoute({
  getParentRoute: () => adminInspectMapLayoutRoute,
  path: "/settings",
  component: MapSettings,
});

const adminMapInspectLayersRoute = createRoute({
  getParentRoute: () => adminInspectMapLayoutRoute,
  path: "/layers",
  component: MapLayers,
});

const adminMapInspectSourcesRoute = createRoute({
  getParentRoute: () => adminInspectMapLayoutRoute,
  path: "/sources",
  component: MapSources,
});

const adminMapInspectVersionRoute = createRoute({
  getParentRoute: () => adminInspectMapLayoutRoute,
  path: "/versions",
  component: MapVersions,
});

const adminMapInspectAIAssistant = createRoute({
  getParentRoute: () => adminInspectMapLayoutRoute,
  path: "/ai",
  component: AIMap,
});

const adminChatInspectSettingsRoute = createRoute({
  getParentRoute: () => adminInspectMapChatLayoutRoute,
  path: "/settings",
  component: MapSettings,
});

const adminChatInspectLayersRoute = createRoute({
  getParentRoute: () => adminInspectMapChatLayoutRoute,
  path: "/layers",
  component: MapLayers,
});

const adminChatInspectSourcesRoute = createRoute({
  getParentRoute: () => adminInspectMapChatLayoutRoute,
  path: "/sources",
  component: MapSources,
});

const adminChatInspectVersionRoute = createRoute({
  getParentRoute: () => adminInspectMapChatLayoutRoute,
  path: "/versions",
  component: MapVersions,
});

const adminChatInspectAIAssistant = createRoute({
  getParentRoute: () => adminInspectMapChatLayoutRoute,
  path: "/ai",
  component: AIMap,
});

export const adminDashboardRoutes = adminDashboardLayoutRoute.addChildren([
  adminIndexRoute,
  adminAccountsRoute,
  adminUsersRoute,
  adminMapsRoute,
  adminUsageRoute,
  adminInspectMapLayoutRoute.addChildren([
    adminMapInspectSettingsRoute,
    adminMapInspectLayersRoute,
    adminMapInspectSourcesRoute,
    adminMapInspectVersionRoute,
    adminMapInspectAIAssistant,
  ]),
  adminChatsRoute,
  adminInspectMapChatLayoutRoute.addChildren([
    adminChatInspectSettingsRoute,
    adminChatInspectLayersRoute,
    adminChatInspectSourcesRoute,
    adminChatInspectVersionRoute,
    adminChatInspectAIAssistant,
  ]),
]);
